import React, { useCallback, useEffect, useState } from "react";
import { apiRequest } from "../../../helpers/api";
import { useAccess } from "../../../components/AccessContext";
import { showError, showSuccess } from "../../../components/ToastNotifications";
import { SkeletonList } from "../../../components/skeleton/Skeleton";
import { ConfirmDialog, Dialog, Field, ImageField, Toggle, assetSrc, formatDateTime } from "./cmsShared";
import "./Website.css";

const placements = [
  { id: "announcement", label: "Announcement bar", icon: "ti-speakerphone", description: "A slim bar above the site header. Several active announcements rotate." },
  { id: "carousel", label: "Header carousel", icon: "ti-carousel-horizontal", description: "Rotating slides with an image, text and a button, shown under the header." },
  { id: "promo", label: "Promo banners", icon: "ti-discount-2", description: "Offer banners shown at the top of the page content on the pages you choose." }
];
const themes = [["brand", "Teal"], ["dark", "Dark"], ["light", "Light"], ["accent", "Mint"]];
const stateLabels = { live: "Live", scheduled: "Scheduled", expired: "Ended", disabled: "Off" };

// <input type="datetime-local"> works in local time without a zone; the API stores UTC.
const toLocalInput = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};
const fromLocalInput = (value) => (value ? new Date(value).toISOString() : null);

const emptyBanner = (placement) => ({
  placement, name: "", title: "", body: "", imageUrl: "", imageAlt: "", ctaLabel: "", ctaUrl: "", ctaNewTab: false,
  theme: "brand", targetPaths: null, dismissible: true, startsAt: null, endsAt: null, enabled: false
});

const scheduleText = (banner) => {
  if (!banner.startsAt && !banner.endsAt) return "No schedule";
  if (banner.startsAt && banner.endsAt) return `${formatDateTime(banner.startsAt)} – ${formatDateTime(banner.endsAt)}`;
  return banner.startsAt ? `From ${formatDateTime(banner.startsAt)}` : `Until ${formatDateTime(banner.endsAt)}`;
};

export const BannerPreview = ({ banner }) => {
  const hasImage = Boolean(banner.imageUrl) && banner.placement !== "announcement";
  return <div className={`cms-banner-preview is-${banner.placement} is-theme-${banner.theme}${hasImage ? " has-image" : ""}`}>
    {hasImage && <img src={assetSrc(banner.imageUrl)} alt="" />}
    <div>
      <strong>{banner.title || <span className="cms-missing">Headline</span>}</strong>
      {banner.body && banner.placement !== "announcement" && <p>{banner.body}</p>}
    </div>
    {banner.ctaLabel && <span className="cms-banner-cta">{banner.ctaLabel}</span>}
    {banner.dismissible && banner.placement !== "carousel" && <i className="ti ti-x" aria-hidden="true" />}
  </div>;
};

const BannerEditor = ({ initial, onClose, onSaved, canPublish, canUpload, canDelete }) => {
  const [form, setForm] = useState(() => ({ ...initial, imageUrl: initial.imageUrl || "", targetText: (initial.targetPaths || []).join("\n") }));
  const [scope, setScope] = useState(initial.targetPaths?.length ? "some" : "all");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (changes) => setForm((current) => ({ ...current, ...changes }));
  const isLive = initial.id && initial.state === "live";
  const readOnly = (isLive || initial.enabled) && !canPublish;

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setErrors({});
    const { targetText, id, state, revision, createdAt, updatedAt, ...rest } = form;
    const body = {
      ...rest,
      imageUrl: rest.imageUrl || null,
      targetPaths: scope === "all" ? null : targetText.split(/[\n,]+/).map((value) => value.trim()).filter(Boolean)
    };
    try {
      const result = await apiRequest(initial.id ? `/api/admin/website/banners/${initial.id}` : "/api/admin/website/banners", { method: initial.id ? "PUT" : "POST", body: JSON.stringify(body) });
      showSuccess(result.message);
      onSaved();
    } catch (error) {
      setErrors(error.data?.errors || {});
      showError(error.message);
      setSaving(false);
    }
  };

  const placement = placements.find((item) => item.id === form.placement);
  return <Dialog title={initial.id ? `Edit ${placement.label.toLowerCase()}` : `New ${placement.label.toLowerCase().replace(/s$/, "")}`} wide onClose={onClose} footer={<>
    <button type="button" className="cms-button" onClick={onClose} disabled={saving}>Cancel</button>
    <button type="submit" form="cms-banner-form" className="cms-button is-primary" disabled={saving || readOnly}>{saving && <i className="ti ti-loader-2 cms-spin" aria-hidden="true" />}Save banner</button>
  </>}>
    {readOnly && <div className="cms-alert is-warning" role="note"><i className="ti ti-lock" aria-hidden="true" /><span>This banner is switched on. Your role can't change banners that are visible on the website.</span></div>}
    <form id="cms-banner-form" onSubmit={submit} noValidate className="cms-banner-form">
      <fieldset disabled={readOnly || saving} className="cms-fieldset">
        <div className="cms-banner-preview-wrap"><span className="cms-muted">Preview</span><BannerPreview banner={form} /></div>
        <div className="cms-grid-2">
          <Field label="Placement" error={errors.placement}>
            <select value={form.placement} onChange={(event) => set({ placement: event.target.value })}>
              {placements.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
            </select>
          </Field>
          <Field label="Internal name" error={errors.name} hint="Only shown in the admin panel." count={form.name.length} max={120}>
            <input value={form.name} onChange={(event) => set({ name: event.target.value })} maxLength={120} placeholder="e.g. Diwali offer 2026" />
          </Field>
        </div>
        <Field label="Headline" error={errors.title} count={form.title.length} max={160}>
          <input value={form.title} onChange={(event) => set({ title: event.target.value })} maxLength={160} />
        </Field>
        {form.placement !== "announcement" && <Field label="Description" error={errors.body} count={form.body.length} max={500}>
          <textarea rows={2} value={form.body} onChange={(event) => set({ body: event.target.value })} maxLength={500} />
        </Field>}
        <div className="cms-grid-2">
          <Field label="Button label" error={errors.ctaLabel} count={form.ctaLabel.length} max={60}>
            <input value={form.ctaLabel} onChange={(event) => set({ ctaLabel: event.target.value })} maxLength={60} placeholder={form.placement === "announcement" ? "Learn more" : ""} />
          </Field>
          <Field label="Button link" error={errors.ctaUrl} hint="/pricing, https://…, mailto: or tel:">
            <input value={form.ctaUrl} onChange={(event) => set({ ctaUrl: event.target.value.trim() })} maxLength={500} spellCheck={false} />
          </Field>
        </div>
        {/^https?:/i.test(form.ctaUrl) && <Toggle checked={form.ctaNewTab} onChange={(ctaNewTab) => set({ ctaNewTab })} label="Open the link in a new tab" />}
        {form.placement !== "announcement" && <ImageField label="Image" value={{ url: form.imageUrl, alt: form.imageAlt }} onChange={(image) => set({ imageUrl: image.url, imageAlt: image.alt })}
          errors={{ url: errors.imageUrl, alt: errors.imageAlt }} canUpload={canUpload} canDelete={canDelete} disabled={readOnly || saving} />}
        <fieldset className="cms-choice-group is-inline">
          <legend>Colour</legend>
          {themes.map(([id, label]) => <label key={id} className={`cms-swatch is-theme-${id}${form.theme === id ? " is-selected" : ""}`}>
            <input type="radio" name="cms-theme" checked={form.theme === id} onChange={() => set({ theme: id })} /><span aria-hidden="true" />{label}
          </label>)}
        </fieldset>
        <div className="cms-grid-2">
          <Field label="Start (optional)" error={errors.startsAt} hint="Your local time. Empty = as soon as it's switched on.">
            <input type="datetime-local" value={toLocalInput(form.startsAt)} onChange={(event) => set({ startsAt: fromLocalInput(event.target.value) })} />
          </Field>
          <Field label="End (optional)" error={errors.endsAt} hint="Empty = until it's switched off.">
            <input type="datetime-local" value={toLocalInput(form.endsAt)} onChange={(event) => set({ endsAt: fromLocalInput(event.target.value) })} />
          </Field>
        </div>
        <fieldset className="cms-choice-group">
          <legend>Show on</legend>
          <label className={`cms-choice${scope === "all" ? " is-selected" : ""}`}><input type="radio" name="cms-scope" checked={scope === "all"} onChange={() => setScope("all")} /><span><strong>Every page</strong></span></label>
          <label className={`cms-choice${scope === "some" ? " is-selected" : ""}`}><input type="radio" name="cms-scope" checked={scope === "some"} onChange={() => setScope("some")} /><span><strong>Specific pages</strong><small>One path per line. End with /* to include child pages, e.g. /features/*. Use / for the home page.</small></span></label>
        </fieldset>
        {scope === "some" && <Field label="Pages" error={errors.targetPaths}>
          <textarea rows={3} value={form.targetText} onChange={(event) => set({ targetText: event.target.value })} placeholder={"/\n/pricing\n/features/*"} spellCheck={false} />
        </Field>}
        {form.placement !== "carousel" && <Toggle checked={form.dismissible} onChange={(dismissible) => set({ dismissible })} label="Visitors can close it" hint="A closed banner stays hidden for that visitor until you edit it." />}
        <Toggle checked={form.enabled} onChange={(enabled) => set({ enabled })} disabled={!canPublish} label="Switched on" hint={canPublish ? "Shown on the website while switched on and inside the schedule." : "Your role can save banners switched off. Someone who can publish turns them on."} />
      </fieldset>
    </form>
  </Dialog>;
};

const WebsiteBanners = () => {
  const { can } = useAccess();
  const canCreate = can("admin.website.create");
  const canEdit = can("admin.website.edit");
  const canDelete = can("admin.website.delete");
  const canPublish = can("admin.website.publish");
  const [banners, setBanners] = useState(null);
  const [placement, setPlacement] = useState("announcement");
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setError("");
    try {
      setBanners((await apiRequest("/api/admin/website/banners")).banners);
    } catch (loadError) {
      setError(loadError.message);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const list = (banners || []).filter((banner) => banner.placement === placement);
  const current = placements.find((item) => item.id === placement);

  const toggle = async (banner) => {
    setBusy(banner.id);
    try {
      const result = await apiRequest(`/api/admin/website/banners/${banner.id}`, { method: "PUT", body: JSON.stringify({ enabled: !banner.enabled }) });
      setBanners((items) => items.map((item) => (item.id === banner.id ? result.banner : item)));
      showSuccess(result.banner.enabled ? (result.banner.state === "live" ? "Banner is live." : `Banner switched on (${stateLabels[result.banner.state].toLowerCase()}).`) : "Banner switched off.");
    } catch (toggleError) {
      showError(toggleError.message);
    } finally {
      setBusy(null);
    }
  };

  const move = async (index, delta) => {
    const ids = list.map((banner) => banner.id);
    const [id] = ids.splice(index, 1);
    ids.splice(index + delta, 0, id);
    setBusy(id);
    try {
      const result = await apiRequest("/api/admin/website/banners/reorder", { method: "PUT", body: JSON.stringify({ placement, ids }) });
      setBanners(result.banners);
    } catch (moveError) {
      showError(moveError.message);
      load();
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    setBusy(deleting.id);
    try {
      await apiRequest(`/api/admin/website/banners/${deleting.id}`, { method: "DELETE" });
      setBanners((items) => items.filter((item) => item.id !== deleting.id));
      showSuccess("Banner deleted.");
      setDeleting(null);
    } catch (deleteError) {
      showError(deleteError.message);
    } finally {
      setBusy(null);
    }
  };

  return <main className="workspace-dashboard cms-page">
    <header className="workspace-dashboard-header cms-header">
      <div>
        <span className="workspace-eyebrow">WEBSITE CMS</span>
        <h1>Banners &amp; carousel</h1>
        <p>Announcement bars, header carousel slides and promotional offers for the public website, with scheduling and page targeting.</p>
      </div>
      {canCreate && <button type="button" className="cms-button is-primary" onClick={() => setEditing(emptyBanner(placement))}><i className="ti ti-plus" aria-hidden="true" />New banner</button>}
    </header>

    <nav className="cms-placement-tabs" role="tablist" aria-label="Banner placement">
      {placements.map((item) => {
        const items = (banners || []).filter((banner) => banner.placement === item.id);
        const live = items.filter((banner) => banner.state === "live").length;
        return <button key={item.id} type="button" role="tab" aria-selected={placement === item.id} className={placement === item.id ? "is-active" : ""} onClick={() => setPlacement(item.id)}>
          <i className={`ti ${item.icon}`} aria-hidden="true" />
          <span><strong>{item.label}</strong><small>{items.length} total · {live} live</small></span>
        </button>;
      })}
    </nav>

    <section className="cms-panel">
      <p className="cms-muted cms-placement-help"><i className="ti ti-info-circle" aria-hidden="true" /> {current.description} {list.length > 1 ? "Banners are shown in list order; use the arrows to reorder." : ""}</p>
      {error && <div className="cms-alert is-error" role="alert"><i className="ti ti-alert-circle" aria-hidden="true" /><span>{error}</span><button type="button" className="cms-button" onClick={load}>Retry</button></div>}
      {!banners && !error && <SkeletonList rows={3} />}
      {banners && !list.length && <div className="cms-empty"><i className={`ti ${current.icon}`} aria-hidden="true" /><strong>No {current.label.toLowerCase()} yet</strong><span>{canCreate ? "Create one to promote an offer or share news." : "Banners created by your team appear here."}</span>
        {canCreate && <button type="button" className="cms-button is-primary" onClick={() => setEditing(emptyBanner(placement))}><i className="ti ti-plus" aria-hidden="true" />New banner</button>}</div>}
      {list.length > 0 && <ol className="cms-banner-list">
        {list.map((banner, index) => <li key={banner.id} className={`is-${banner.state}`}>
          <div className="cms-banner-order">
            <button type="button" className="cms-icon-button is-small" onClick={() => move(index, -1)} disabled={index === 0 || !canEdit || busy !== null} aria-label={`Move ${banner.name} up`}><i className="ti ti-chevron-up" aria-hidden="true" /></button>
            <span>{index + 1}</span>
            <button type="button" className="cms-icon-button is-small" onClick={() => move(index, 1)} disabled={index === list.length - 1 || !canEdit || busy !== null} aria-label={`Move ${banner.name} down`}><i className="ti ti-chevron-down" aria-hidden="true" /></button>
          </div>
          <BannerPreview banner={banner} />
          <div className="cms-banner-info">
            <strong>{banner.name}</strong>
            <span className={`cms-pill is-${banner.state}`}>{stateLabels[banner.state]}</span>
            <small><i className="ti ti-calendar" aria-hidden="true" />{scheduleText(banner)}</small>
            <small><i className="ti ti-route" aria-hidden="true" />{banner.targetPaths?.length ? banner.targetPaths.join(", ") : "Every page"}</small>
          </div>
          <div className="cms-banner-actions">
            {canPublish && <label className="cms-toggle is-compact" title={banner.enabled ? "Switch off" : "Switch on"}>
              <input type="checkbox" checked={banner.enabled} onChange={() => toggle(banner)} disabled={busy === banner.id} aria-label={`${banner.enabled ? "Switch off" : "Switch on"} ${banner.name}`} />
              <span className="cms-switch" aria-hidden="true" />
            </label>}
            {(canEdit || canPublish) && <button type="button" className="cms-button is-small" onClick={() => setEditing(banner)}><i className="ti ti-pencil" aria-hidden="true" />Edit</button>}
            {canDelete && <button type="button" className="cms-icon-button is-small" onClick={() => setDeleting(banner)} aria-label={`Delete ${banner.name}`} disabled={banner.state === "live" && !canPublish}><i className="ti ti-trash" aria-hidden="true" /></button>}
          </div>
        </li>)}
      </ol>}
    </section>

    {editing && <BannerEditor initial={editing} canPublish={canPublish} canUpload={canCreate} canDelete={canDelete} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    {deleting && <ConfirmDialog title="Delete this banner?" danger confirmLabel="Delete banner" busy={busy === deleting.id} onClose={() => setDeleting(null)} onConfirm={remove}
      message={`"${deleting.name}" will be permanently deleted${deleting.state === "live" ? " and removed from the website" : ""}.`} />}
  </main>;
};

export default WebsiteBanners;
